import { useEffect, useState } from 'react';
import {
  getCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  isMissingTable,
} from '../services/adminApi';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import FormModal from '../components/FormModal';
import ConfirmDialog from '../components/ConfirmDialog';
import MissingTable from '../components/MissingTable';

const EMPTY_FORM = {
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: '',
  max_discount: '',
  min_order_amount: '',
  usage_limit: '',
  starts_at: '', // yyyy-mm-dd, blank = live immediately
  expires_at: '', // yyyy-mm-dd, blank = never expires
  is_active: true,
};

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/* Dates are edited a day at a time — nobody schedules a sale to the
   minute. A start date opens the code at midnight; an expiry date keeps
   it alive to the end of that day, which is what "valid until 30 Sep"
   means to a customer. Both are converted in the shop's own timezone. */
const toDateInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const startOfDay = (value) =>
  value ? new Date(`${value}T00:00:00`).toISOString() : null;

const endOfDay = (value) =>
  value ? new Date(`${value}T23:59:59`).toISOString() : null;

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

/* A number field left blank means "no limit", which is not the same as
   zero — send null so the database reads it that way. */
const numberOrNull = (value) => {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

const describeDiscount = (c) =>
  c.discount_type === 'fixed'
    ? `${inr(c.discount_value)} off`
    : `${Number(c.discount_value) || 0}% off${
        Number(c.max_discount) > 0 ? ` (max ${inr(c.max_discount)})` : ''
      }`;

/* Why a code isn't currently working, in the shop owner's words. The
   same conditions validate_coupon() checks, so what this says and what
   a customer sees can't drift apart. */
const liveState = (c) => {
  const now = Date.now();

  if (!c.is_active) return 'inactive';
  if (c.starts_at && now < new Date(c.starts_at).getTime()) return 'scheduled';
  if (c.expires_at && now > new Date(c.expires_at).getTime()) return 'expired';
  if (c.usage_limit !== null && Number(c.used_count || 0) >= c.usage_limit) {
    return 'used up';
  }
  return 'active';
};

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await getCoupons();
    setCoupons(data);
    setLoadError(error);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code || '',
      description: c.description || '',
      discount_type: c.discount_type || 'percent',
      discount_value: c.discount_value ?? '',
      max_discount: c.max_discount ?? '',
      min_order_amount: c.min_order_amount ?? '',
      usage_limit: c.usage_limit ?? '',
      starts_at: toDateInput(c.starts_at),
      expires_at: toDateInput(c.expires_at),
      is_active: c.is_active !== false,
    });
    setFormError('');
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setFormError('');

    const code = form.code.trim().toUpperCase();
    if (!code) return setFormError('Give the code a name, e.g. DIWALI10.');
    if (/\s/.test(code))
      return setFormError('A code can’t contain spaces — customers type it in.');

    const value = Number(form.discount_value);
    if (!Number.isFinite(value) || value <= 0)
      return setFormError('Enter a discount value greater than zero.');
    if (form.discount_type === 'percent' && value > 100)
      return setFormError('A percentage discount can’t be more than 100%.');

    const clash = coupons.find((c) => c.code === code && c.id !== editing?.id);
    if (clash) return setFormError(`The code ${code} already exists.`);

    if (form.starts_at && form.expires_at && form.expires_at < form.starts_at)
      return setFormError('The end date is before the start date.');

    setSaving(true);

    const payload = {
      code,
      description: form.description.trim() || null,
      discount_type: form.discount_type,
      discount_value: value,
      // A cap only means anything on a percentage.
      max_discount:
        form.discount_type === 'percent' ? numberOrNull(form.max_discount) : null,
      min_order_amount: numberOrNull(form.min_order_amount) ?? 0,
      usage_limit: numberOrNull(form.usage_limit),
      starts_at: startOfDay(form.starts_at),
      expires_at: endOfDay(form.expires_at),
      is_active: form.is_active,
    };

    const { error } = editing
      ? await updateCoupon(editing.id, payload)
      : await createCoupon(payload);

    setSaving(false);

    if (error) {
      setFormError(`Could not save: ${error.message}`);
      return;
    }

    setModalOpen(false);
    load();
  };

  const toggleActive = async (c) => {
    // Optimistic: the switch should follow the finger, not the network.
    setCoupons((prev) =>
      prev.map((row) =>
        row.id === c.id ? { ...row, is_active: !row.is_active } : row
      )
    );

    const { error } = await updateCoupon(c.id, { is_active: !c.is_active });
    if (error) {
      alert(`Update failed: ${error.message}`);
      load();
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    const { error } = await deleteCoupon(deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    if (error) alert(`Delete failed: ${error.message}`);
    else load();
  };

  if (!loading && isMissingTable(loadError)) {
    return <MissingTable table="coupons" file="src/backend/sql/coupons.sql" />;
  }

  const live = coupons.filter((c) => liveState(c) === 'active').length;

  return (
    <div>
      <div className="page-head">
        <h1>Coupons</h1>
        <button className="btn-primary" onClick={openAdd}>
          + New Code
        </button>
      </div>

      {loadError && !isMissingTable(loadError) && (
        <p className="admin-error">
          Failed to load coupons: {loadError.message}
        </p>
      )}

      {coupons.length > 0 && (
        <p className="hint">
          {live} of {coupons.length} code{coupons.length === 1 ? '' : 's'}{' '}
          usable right now. Customers enter these in the cart.
        </p>
      )}

      <DataTable
        loading={loading}
        emptyMessage="No discount codes yet. Click “+ New Code” to create one."
        rows={coupons}
        columns={[
          {
            key: 'code',
            label: 'Code',
            render: (c) => (
              <div>
                <div className="cell-title">{c.code}</div>
                {c.description && (
                  <div className="cell-sub">{c.description}</div>
                )}
              </div>
            ),
          },
          {
            key: 'discount_value',
            label: 'Discount',
            render: (c) => (
              <div>
                <div className="cell-title">{describeDiscount(c)}</div>
                {Number(c.min_order_amount) > 0 && (
                  <div className="cell-sub">
                    on orders over {inr(c.min_order_amount)}
                  </div>
                )}
              </div>
            ),
          },
          {
            key: 'used_count',
            label: 'Used',
            render: (c) => (
              <span className="cell-sub">
                {Number(c.used_count || 0)}
                {c.usage_limit !== null && c.usage_limit !== undefined
                  ? ` / ${c.usage_limit}`
                  : ' times'}
              </span>
            ),
          },
          {
            key: 'expires_at',
            label: 'Valid',
            render: (c) => {
              const from = fmtDate(c.starts_at);
              const until = fmtDate(c.expires_at);

              return (
                <span className="cell-sub">
                  {from && until
                    ? `${from} – ${until}`
                    : until
                      ? `until ${until}`
                      : from
                        ? `from ${from}`
                        : 'no end date'}
                </span>
              );
            },
          },
          {
            key: 'state',
            label: 'State',
            render: (c) => <StatusBadge status={liveState(c)} />,
          },
          {
            key: 'is_active',
            label: 'On',
            render: (c) => (
              <button
                className={`toggle ${c.is_active ? 'on' : ''}`}
                onClick={() => toggleActive(c)}
                title={
                  c.is_active
                    ? 'Accepted at checkout — click to switch off'
                    : 'Switched off — click to accept it again'
                }
              >
                <span className="knob" />
              </button>
            ),
          },
          {
            key: 'actions',
            label: 'Actions',
            render: (c) => (
              <div className="action-buttons">
                <button className="btn-edit" onClick={() => openEdit(c)}>
                  Edit
                </button>
                <button
                  className="btn-delete"
                  onClick={() => setDeleteTarget(c)}
                >
                  Delete
                </button>
              </div>
            ),
          },
        ]}
      />

      <FormModal
        open={modalOpen}
        title={editing ? `Edit — ${editing.code}` : 'New Discount Code'}
        onClose={() => setModalOpen(false)}
      >
        <form onSubmit={save} className="admin-form">
          <label>
            Code *
            <input
              value={form.code}
              onChange={(e) => set('code', e.target.value.toUpperCase())}
              placeholder="DIWALI10"
              autoCapitalize="characters"
              spellCheck="false"
            />
            <p className="hint">
              Case doesn’t matter to the customer — “diwali10” works too.
            </p>
          </label>

          <label>
            Description
            <input
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Diwali festival offer"
            />
            <p className="hint">For your own reference in this list.</p>
          </label>

          <div className="form-grid">
            <label>
              Type
              <select
                value={form.discount_type}
                onChange={(e) => set('discount_type', e.target.value)}
              >
                <option value="percent">Percentage off</option>
                <option value="fixed">Flat ₹ off</option>
              </select>
            </label>

            <label>
              {form.discount_type === 'percent'
                ? 'Percentage *'
                : 'Amount off (₹) *'}
              <input
                type="number"
                min="0"
                step={form.discount_type === 'percent' ? '1' : '10'}
                value={form.discount_value}
                onChange={(e) => set('discount_value', e.target.value)}
                placeholder={form.discount_type === 'percent' ? '10' : '200'}
              />
            </label>
          </div>

          {form.discount_type === 'percent' && (
            <label>
              Maximum discount (₹)
              <input
                type="number"
                min="0"
                step="50"
                value={form.max_discount}
                onChange={(e) => set('max_discount', e.target.value)}
                placeholder="Leave blank for no cap"
              />
              <p className="hint">
                Caps what the percentage can take off a large order.
              </p>
            </label>
          )}

          <div className="form-grid">
            <label>
              Minimum order (₹)
              <input
                type="number"
                min="0"
                step="100"
                value={form.min_order_amount}
                onChange={(e) => set('min_order_amount', e.target.value)}
                placeholder="0"
              />
            </label>

            <label>
              Total uses allowed
              <input
                type="number"
                min="1"
                step="1"
                value={form.usage_limit}
                onChange={(e) => set('usage_limit', e.target.value)}
                placeholder="Unlimited"
              />
            </label>
          </div>

          <div className="form-grid">
            <label>
              Starts on
              <input
                type="date"
                value={form.starts_at}
                onChange={(e) => set('starts_at', e.target.value)}
              />
            </label>

            <label>
              Valid until
              <input
                type="date"
                value={form.expires_at}
                onChange={(e) => set('expires_at', e.target.value)}
              />
            </label>
          </div>
          <p className="hint">
            Leave both blank for a code that works from now on, with no end
            date. The last day counts in full.
          </p>

          <label className="check">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
            />
            Accept this code at checkout
          </label>

          {editing && (
            <p className="hint">
              Redeemed {Number(editing.used_count || 0)} time
              {Number(editing.used_count || 0) === 1 ? '' : 's'} so far.
            </p>
          )}

          {formError && <p className="admin-error">{formError}</p>}

          <div className="admin-modal-actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Code'}
            </button>
          </div>
        </form>
      </FormModal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this code?"
        message={
          `"${deleteTarget?.code}" will stop working immediately. ` +
          'Orders already placed with it keep their discount. To pause it ' +
          'instead, switch it off in the On column.'
        }
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
